/**
 * PWA Analytics Tracking Service
 * Tracks user sessions, page views, and engagement events
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate or retrieve anonymous ID
const getAnonymousId = () => {
  let anonymousId = localStorage.getItem('lokalgrow_anonymous_id');
  if (!anonymousId) {
    anonymousId = 'anon_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem('lokalgrow_anonymous_id', anonymousId);
  }
  return anonymousId;
};

// Generate session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('lokalgrow_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    sessionStorage.setItem('lokalgrow_session_id', sessionId);
    sessionStorage.setItem('lokalgrow_session_start', Date.now().toString());
  }
  return sessionId;
};

// Detect device info
const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  
  // Device type
  let deviceType = 'desktop';
  if (/Mobi|Android/i.test(ua)) {
    deviceType = /iPad|Tablet/i.test(ua) ? 'tablet' : 'mobile';
  }
  
  // OS detection
  let os = 'unknown';
  if (/Windows/i.test(ua)) os = 'Windows';
  else if (/Mac OS/i.test(ua)) os = 'macOS';
  else if (/Linux/i.test(ua)) os = 'Linux';
  else if (/Android/i.test(ua)) os = 'Android';
  else if (/iOS|iPhone|iPad/i.test(ua)) os = 'iOS';
  
  // Browser detection
  let browser = 'unknown';
  if (/Chrome/i.test(ua) && !/Edg/i.test(ua)) browser = 'Chrome';
  else if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) browser = 'Safari';
  else if (/Firefox/i.test(ua)) browser = 'Firefox';
  else if (/Edg/i.test(ua)) browser = 'Edge';
  
  return {
    deviceType,
    os,
    browser,
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight
  };
};

// Check if running as installed PWA
const isPWA = () => {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true ||
         document.referrer.includes('android-app://');
};

// Check if analytics is enabled (user preference)
const isAnalyticsEnabled = () => {
  const preference = localStorage.getItem('lokalgrow_analytics_enabled');
  return preference !== 'false'; // Default to enabled
};

// Session duration tracking
let sessionStartTime = Date.now();
let lastPageViewTime = Date.now();

class PWAAnalyticsService {
  constructor() {
    this.anonymousId = getAnonymousId();
    this.sessionId = getSessionId();
    this.deviceInfo = getDeviceInfo();
    this.isPWA = isPWA();
    this.queue = [];
    this.isProcessing = false;
    
    // Track session start
    this.trackEvent('session_start');
    
    // Track PWA launch if applicable
    if (this.isPWA) {
      this.trackEvent('pwa_launched');
    }
    
    // Handle page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.trackEvent('session_end', {
          session_duration_ms: Date.now() - sessionStartTime
        });
      }
    });
    
    // Handle beforeunload
    window.addEventListener('beforeunload', () => {
      this.trackEvent('session_end', {
        session_duration_ms: Date.now() - sessionStartTime
      }, true); // Use beacon API
    });
  }
  
  /**
   * Track an analytics event
   * @param {string} eventType - Type of event (page_view, kupon_view, etc.)
   * @param {object} metadata - Additional event data
   * @param {boolean} useBeacon - Use navigator.sendBeacon for reliability
   */
  trackEvent(eventType, metadata = {}, useBeacon = false) {
    if (!isAnalyticsEnabled()) return;
    
    const event = {
      event_type: eventType,
      page_path: window.location.pathname,
      referrer: document.referrer,
      session_id: this.sessionId,
      is_pwa: this.isPWA,
      is_standalone: window.matchMedia('(display-mode: standalone)').matches,
      device_type: this.deviceInfo.deviceType,
      os: this.deviceInfo.os,
      browser: this.deviceInfo.browser,
      screen_width: this.deviceInfo.screenWidth,
      screen_height: this.deviceInfo.screenHeight,
      session_duration_ms: Date.now() - sessionStartTime,
      time_on_page_ms: Date.now() - lastPageViewTime,
      metadata
    };
    
    if (eventType === 'page_view') {
      lastPageViewTime = Date.now();
    }
    
    if (useBeacon && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(event)], { type: 'application/json' });
      navigator.sendBeacon(`${API_URL}/api/analytics/pwa/track`, blob);
    } else {
      this.queue.push(event);
      this.processQueue();
    }
  }
  
  async processQueue() {
    if (this.isProcessing || this.queue.length === 0) return;
    
    this.isProcessing = true;
    
    while (this.queue.length > 0) {
      const event = this.queue.shift();
      
      try {
        const user = JSON.parse(localStorage.getItem('user') || 'null');
        
        await fetch(`${API_URL}/api/analytics/pwa/track`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Anonymous-Id': this.anonymousId,
            'X-User-Id': user?.id || ''
          },
          body: JSON.stringify(event)
        });
      } catch (error) {
        // Re-queue on failure (max 3 retries)
        if (!event._retries) event._retries = 0;
        if (event._retries < 3) {
          event._retries++;
          this.queue.push(event);
        }
        console.warn('PWA Analytics: Failed to track event', error);
      }
    }
    
    this.isProcessing = false;
  }
  
  // Convenience methods for common events
  trackPageView(pagePath) {
    this.trackEvent('page_view', { custom_path: pagePath });
  }
  
  trackKuponView(kuponId, kuponTitle) {
    this.trackEvent('kupon_view', { kupon_id: kuponId, kupon_title: kuponTitle });
  }
  
  trackKuponReserve(kuponId, kuponTitle, amount) {
    this.trackEvent('kupon_reserve', { kupon_id: kuponId, kupon_title: kuponTitle, amount });
  }
  
  trackKuponRedeem(kuponId) {
    this.trackEvent('kupon_redeem', { kupon_id: kuponId });
  }
  
  trackRewardView(rewardId, rewardTitle) {
    this.trackEvent('reward_view', { reward_id: rewardId, reward_title: rewardTitle });
  }
  
  trackRewardClaim(rewardId, pointsCost) {
    this.trackEvent('reward_claim', { reward_id: rewardId, points_cost: pointsCost });
  }
  
  trackBusinessView(businessId, businessName) {
    this.trackEvent('business_view', { business_id: businessId, business_name: businessName });
  }
  
  trackSearch(query, resultsCount) {
    this.trackEvent('search_performed', { query, results_count: resultsCount });
  }
  
  trackSignup(method = 'email') {
    this.trackEvent('signup_completed', { method });
  }
  
  trackLogin(method = 'email') {
    this.trackEvent('login', { method });
  }
  
  trackPWAPromptShown() {
    this.trackEvent('pwa_prompt_shown');
  }
  
  trackPWAPromptAccepted() {
    this.trackEvent('pwa_prompt_accepted');
  }
  
  trackPWAPromptDismissed() {
    this.trackEvent('pwa_prompt_dismissed');
  }
  
  trackPWAInstalled() {
    this.trackEvent('pwa_installed');
  }
  
  // Custom event for any other tracking
  trackCustomEvent(eventName, data = {}) {
    this.trackEvent('custom', { event_name: eventName, ...data });
  }
  
  // Opt-out management
  async setOptOut(optOut) {
    localStorage.setItem('lokalgrow_analytics_enabled', optOut ? 'false' : 'true');
    
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (user?.id) {
      try {
        await fetch(`${API_URL}/api/analytics/pwa/opt-out?enabled=${!optOut}`, {
          method: 'POST',
          headers: { 'X-User-Id': user.id }
        });
      } catch (error) {
        console.warn('Failed to sync opt-out preference', error);
      }
    }
  }
  
  isOptedOut() {
    return !isAnalyticsEnabled();
  }
}

// Create singleton instance
let analyticsInstance = null;

export const initPWAAnalytics = () => {
  if (!analyticsInstance) {
    analyticsInstance = new PWAAnalyticsService();
  }
  return analyticsInstance;
};

export const pwaAnalytics = {
  get instance() {
    if (!analyticsInstance) {
      initPWAAnalytics();
    }
    return analyticsInstance;
  }
};

// React hook for analytics
export const usePWAAnalytics = () => {
  return pwaAnalytics.instance;
};

export default pwaAnalytics;
